import { Disclosure } from "@headlessui/react";
import classNames from "clsx";
import request from "graphql-request";
import { GetStaticPaths, GetStaticPropsContext } from "next";
import Head from "next/head";
import { useTranslations } from "next-intl";
import { HiChevronDown } from "react-icons/hi";

import { TextBlock } from "@shared/components";
import { urls } from "@shared/constants";
import { Text } from "@shared/elements";
import { docTitle } from "@shared/utils";

import { FaqCallPage, TFaqCallPage } from "__generated__/types";

import * as constants from "src/constants";
import bundleMessages from "src/utils/bundleMessages";
import { getLanguageDirection } from "src/utils/getLanguageDirection";

type Props = {
  data: TFaqCallPage;
};

export default function Faq({ data }: Props) {
  const t = useTranslations(`${constants.MESSAGE_PREFIX}pages.Faq`);

  return (
    <>
      <Head>
        <title key="title">
          {docTitle(t("documentTitle"), data.callPage?.call.title.en)}
        </title>
        <meta
          property="og:title"
          content={docTitle(t("documentTitle"), data.callPage?.call.title.en)}
          key="og-title"
        />
        <meta
          property="og:url"
          content={`${urls.callPage(data.callPage?.slug ?? "")}/faq`}
          key="og-url"
        />
      </Head>
      <div
        className="flex flex-col items-center mx-auto py-10 lg:py-14"
        dir={getLanguageDirection(data.callPage?.language)}
      >
        <div className="w-full max-w-[700px] px-4 md:px-0 mx-auto divide-y-[1px] divide-gray-200">
          <Text as="h2" size="3xl" weight="bold">
            {t("title")}
          </Text>
          <dl className="mt-8 space-y-6 divide-y divide-gray-200">
            {data.callPage?.faq?.map((faq) => (
              <Disclosure as="div" key={faq.question} className="pt-6">
                {({ open }) => (
                  <>
                    <dt className="text-lg">
                      <Disclosure.Button className="text-left rtl:text-right w-full flex justify-between items-start text-gray-400">
                        <Text
                          as="span"
                          size="lg"
                          weight="medium"
                          color="custom"
                          className="text-black"
                        >
                          {faq.question}
                        </Text>
                        <span className="ml-6 h-7 flex items-center">
                          <HiChevronDown
                            className={classNames(
                              open ? "-rotate-180" : "rotate-0",
                              "h-6 w-6 transform",
                            )}
                            aria-hidden="true"
                          />
                        </span>
                      </Disclosure.Button>
                    </dt>
                    <Disclosure.Panel
                      as="dd"
                      className="mt-4 pr-12 rtl:pr-0 rtl:pl-12"
                    >
                      <TextBlock content={faq.answer ?? ""} />
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
            ))}
          </dl>
        </div>
      </div>
    </>
  );
}

export const getStaticPaths: GetStaticPaths<{ id: string }> = async () => {
  return {
    paths: [],
    fallback: "blocking",
  };
};

export async function getStaticProps({
  locale,
  params,
}: GetStaticPropsContext) {
  const messages = await bundleMessages(locale, [
    `${constants.MESSAGE_PREFIX}pages.Faq`,
  ]);

  const slug: string = params?.slug as string;

  const data: TFaqCallPage | null = await request(
    `${process.env.NEXT_PUBLIC_API_URL}/graphql`,
    FaqCallPage,
    { slug },
  );

  if (!data?.callPage) {
    return {
      notFound: true,
      revalidate: 60,
    };
  }
  return {
    props: {
      data,
      messages,
    },
    revalidate: 60,
  };
}
